import { useState, useMemo } from 'react';
import {
  Building2,
  Plus,
  Search,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Edit2,
  Palette,
  Power,
  Shield,
  FileText,
  ShoppingBag,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table.jsx';
import { TenantModal } from './TenantModal.jsx';

export function TenantsTab({
  tenants = [],
  onSelectTenant,
  onCreateTenant,
  onUpdateTenant,
  onSwitchTab,
  loading = false,
  saving = false,
  error = null,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [modalError, setModalError] = useState(null);

  // Compute metrics summary
  const metrics = useMemo(() => {
    const total = tenants.length;
    const active = tenants.filter((t) => t.isActive !== false && t.isActive !== 0).length;
    const inquiries = tenants.reduce((acc, t) => acc + (t.metrics?.inquiries || 0), 0);
    const orders = tenants.reduce((acc, t) => acc + (t.metrics?.orders || 0), 0);
    return { total, active, inquiries, orders };
  }, [tenants]);

  // Filtered tenants list
  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return tenants;
    const q = searchQuery.toLowerCase().trim();
    return tenants.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.slug?.toLowerCase().includes(q) ||
        t.domain?.toLowerCase().includes(q) ||
        t.contactEmail?.toLowerCase().includes(q)
    );
  }, [tenants, searchQuery]);

  const handleOpenCreate = () => {
    setEditingTenant(null);
    setModalError(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (tenant) => {
    setEditingTenant(tenant);
    setModalError(null);
    setModalOpen(true);
  };

  const handleSaveTenant = async (payload) => {
    try {
      setModalError(null);
      if (editingTenant) {
        await onUpdateTenant(editingTenant.id, payload);
      } else {
        const created = await onCreateTenant(payload);
        if (created && onSelectTenant) {
          onSelectTenant(created);
        }
        if (onSwitchTab) {
          onSwitchTab('theme');
        }
      }
      setModalOpen(false);
    } catch (err) {
      setModalError(err.message || 'Fehler beim Speichern des Mandanten');
    }
  };

  const handleToggleActive = async (tenant) => {
    try {
      const newActive = tenant.isActive ? 0 : 1;
      await onUpdateTenant(tenant.id, { isActive: newActive });
    } catch (err) {
      console.error('Error toggling tenant status:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-slate-200 bg-white shadow-xs rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Mandanten Gesamt
            </CardTitle>
            <Building2 className="h-4 w-4 text-[var(--primary)]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{metrics.total}</div>
            <p className="text-xs text-slate-500 mt-1">Registrierte Partner-Shops</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-xs rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Aktive Shops
            </CardTitle>
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold text-emerald-700 tabular-nums">{metrics.active}</div>
            <p className="text-xs text-slate-500 mt-1">
              {metrics.total > 0 ? `${Math.round((metrics.active / metrics.total) * 100)}% Aktivitätsrate` : '0%'}
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-xs rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              B2B Anfragen
            </CardTitle>
            <FileText className="h-4 w-4 text-slate-700" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{metrics.inquiries}</div>
            <p className="text-xs text-slate-500 mt-1">Über alle Mandanten</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-xs rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Bestellungen
            </CardTitle>
            <ShoppingBag className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{metrics.orders}</div>
            <p className="text-xs text-slate-500 mt-1">Direkte Shop-Aufträge</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Bar: Search, Brand-Isolation Badge & Create */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Mandanten suchen (Name, Slug, Domain)..."
              className="pl-9 text-xs"
            />
          </div>

          <Badge variant="outline" className="hidden sm:inline-flex gap-1.5 border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] py-1 px-2.5">
            <Shield className="h-3.5 w-3.5 text-emerald-600" />
            <span>Mandanten- & Daten-Isolation aktiv</span>
          </Badge>
        </div>

        <Button onClick={handleOpenCreate} className="gap-2 shrink-0">
          <Plus className="h-4 w-4" />
          Neuer Partner-Mandant
        </Button>
      </div>

      {/* Tenants Table */}
      <Card className="border-[var(--border)] bg-white shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="font-semibold text-xs">Mandant</TableHead>
                <TableHead className="font-semibold text-xs">Slug & ID</TableHead>
                <TableHead className="font-semibold text-xs">Shop-Domain</TableHead>
                <TableHead className="font-semibold text-xs">Farben</TableHead>
                <TableHead className="font-semibold text-xs">Status</TableHead>
                <TableHead className="font-semibold text-xs text-right">Anfragen / Orders</TableHead>
                <TableHead className="font-semibold text-xs text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-slate-500">
                    {searchQuery ? 'Keine Mandanten passend zur Suchanfrage gefunden.' : 'Keine Mandanten vorhanden.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map((t) => {
                  const isActive = t.isActive !== false && t.isActive !== 0;
                  return (
                    <TableRow key={t.id} className="hover:bg-slate-50/60 transition-colors">
                      {/* Name & Contact */}
                      <TableCell>
                        <div className="font-semibold text-sm text-[var(--foreground)]">{t.name}</div>
                        <div className="text-xs text-slate-500">{t.contactEmail || 'Keine E-Mail'}</div>
                      </TableCell>

                      {/* Slug & ID */}
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-[11px]">
                          {t.slug}
                        </Badge>
                        <div className="text-[10px] font-mono text-slate-400 mt-0.5">{t.id}</div>
                      </TableCell>

                      {/* Domain */}
                      <TableCell>
                        <div className="flex items-center gap-1.5 font-mono text-xs text-slate-700">
                          <span>{t.domain}</span>
                        </div>
                      </TableCell>

                      {/* Colors */}
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span
                            className="h-5 w-5 rounded-full border border-slate-300 shadow-2xs"
                            style={{ backgroundColor: t.primaryColor || '#0056b3' }}
                            title={`Primär: ${t.primaryColor || '#0056b3'}`}
                          />
                          <span
                            className="h-5 w-5 rounded-full border border-slate-300 shadow-2xs"
                            style={{ backgroundColor: t.secondaryColor || '#5f5e5f' }}
                            title={`Sekundär: ${t.secondaryColor || '#5f5e5f'}`}
                          />
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        {isActive ? (
                          <Badge variant="success" className="gap-1 text-[11px]">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Aktiv
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1 text-[11px] text-slate-500 bg-slate-100">
                            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                            Inaktiv
                          </Badge>
                        )}
                      </TableCell>

                      {/* Metrics */}
                      <TableCell className="text-right">
                        <div className="text-xs font-semibold text-slate-800">
                          {t.metrics?.inquiries || 0} Anfragen
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {t.metrics?.orders || 0} Bestellungen
                        </div>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Edit Details */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(t)}
                            title="Mandant bearbeiten"
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>

                          {/* Customize Theme & Live Preview */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (onSelectTenant) onSelectTenant(t);
                              if (onSwitchTab) onSwitchTab('theme');
                            }}
                            title="Live-Vorschau & Theme anpassen"
                            className="h-8 px-2 gap-1 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 font-medium"
                          >
                            <Palette className="h-3.5 w-3.5" />
                            <span className="hidden lg:inline text-[11px]">Vorschau</span>
                          </Button>

                          {/* Toggle Active */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(t)}
                            title={isActive ? 'Deaktivieren' : 'Aktivieren'}
                            className={`h-8 w-8 p-0 ${
                              isActive ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'
                            }`}
                          >
                            <Power className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Tenant Modal (Create/Edit) */}
      <TenantModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        tenant={editingTenant}
        onSave={handleSaveTenant}
        saving={saving}
        error={modalError}
      />
    </div>
  );
}
